import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, Container, TextField, Button, MenuItem, Select, InputLabel, FormControl, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const UploadNotesScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { schoolId, teacherId } = state || {};
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedClass, setSelectedClass] = useState('Grade 6');
  const [selectedSubject, setSelectedSubject] = useState('Mathematics');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const classes = [
    'PP1', 'PP2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];
  const subjects = [
    'Mathematics', 'English', 'Kiswahili', 'Science', 'Social Studies',
    'CRE', 'IRE', 'History', 'Geography', 'Business', 'Agriculture'
  ];

  if (!schoolId || !teacherId) {
    navigate(-1);
    return null;
  }

  const handleFilePick = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile({ file, name: file.name, type: file.type });
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('class', selectedClass);
    formData.append('subject', selectedSubject);
    formData.append('schoolId', schoolId);
    formData.append('teacherId', teacherId);
    formData.append('file', file.file, file.name || 'notes.pdf');

    try {
      await axios.post(
        'https://kidsm.vercel.app/api/schools/upload-notes',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      alert('Notes uploaded successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Error uploading notes:', error);
      alert(`Failed to upload notes: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 8 }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', color: '#333', mb: 4 }}
          >
            Upload Notes
          </Typography>
          <Box sx={{ bgcolor: 'white', p: 3, borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                label="Class"
              >
                {classes.map((className) => (
                  <MenuItem key={className} value={className}>
                    {className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                label="Subject"
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              component="label"
              sx={{ bgcolor: '#6200ee', mb: 3, py: 2 }}
            >
              Select File (PDF/DOC/Image)
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                hidden
                onChange={handleFilePick}
              />
            </Button>
            {file && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#333' }}>{file.name}</Typography>
                <Typography sx={{ color: '#666', fontSize: '14px' }}>
                  Click to select a different file
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              fullWidth
              sx={{ bgcolor: '#6200ee', py: 2 }}
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? `Uploading... ${uploadProgress}%` : 'Upload Notes'}
              {loading && <CircularProgress size={20} sx={{ ml: 2, color: '#fff' }} />}
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default UploadNotesScreen;